import React, { useRef, useState } from 'react';
import StableMuiDialog from '../../../../common/StableMuiDialog.js';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import CircularProgress from '@mui/material/CircularProgress';
import SendIcon from '@mui/icons-material/Send';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import FormControlLabel from '@mui/material/FormControlLabel';
import Switch from '@mui/material/Switch';
import Collapse from '@mui/material/Collapse';
import { usePersistedDataStore } from '../../../store.js';
import UnifiedCancelDialog from '../../CancelOffers/UnifiedCancelDialog.js';

/**
 * Centralized dialogs component for Case Review functionality
 * Houses all dialogs to keep the main components clean
 */
const CaseReviewDialogs = ({
  // Optional single-state controller for offer flow dialogs
  offerFlowDialog,
  setOfferFlowDialog,
  offerFlowSelectedRows = [],
  offerFlowOpenCases = [],
  offerFlowMissingOffersList = [],
  offerFlowMissingCompsList = [],
  onMissingCompsSet,

  // Missing Offers Dialog
  missingOffersDialogOpen,
  setMissingOffersDialogOpen,
  missingOffersList,
  handleSetOffersToWorkup,
  updatingOffersToWorkup = false,
  
  // Missing Comps Dialog
  missingCompsDialogOpen,
  setMissingCompsDialogOpen,
  missingCompsList,
  handleProceedWithMissingComps,
  
  // Missing Workup Values Dialog
  missingWorkupDialogOpen,
  setMissingWorkupDialogOpen,
  missingWorkupList,

  workupsAboveIfmvDialogOpen,
  setWorkupsAboveIfmvDialogOpen,
  workupsAboveIfmvList = [],
  handleContinueWorkupsAboveIfmv,

  
  // Subset Warning Dialog
  subsetWarningDialogOpen,
  setSubsetWarningDialogOpen,
  selectedRows,
  openCases,
  emailingOffers,
  handleSendSelectedClick,
  handleSendAllFinalized,
  
  // Send Selected Confirmation Dialog
  sendSelectedConfirmDialogOpen,
  setSendSelectedConfirmDialogOpen,
  handleConfirmSendSelected,
  
  // Unified Cancel Dialog
  cancellingOffers,
  rowsToCancel,
  unifiedCancelDialogOpen,
  setUnifiedCancelDialogOpen,
  handleUnifiedCancelConfirm,
  
  // Upload Dialog
  uploadDialogOpen,
  setUploadDialogOpen,
  onFileUpload,
  onUpdateOffers,
  townView,
  
  // Do not file at SCAR dialog
  assignmentDialogOpen,
  setAssignmentDialogOpen,
  DoNotFileScarDialog,
  
  // Email Preview Dialog
  emailPreviewDialogComponent,
  
  // Offer Resend Dialog
  resendDialogOpen,
  setResendDialogOpen,
  alreadySentCount,
  handleResendConfirm,
  
}) => {
  const offerFlowDialogTypeRef = useRef(offerFlowDialog?.type);
  // Keep in sync during render (avoid useEffect timing races with `onClose` callbacks).
  offerFlowDialogTypeRef.current = offerFlowDialog?.type;

  // Access store for emailing state
  const setEmailingOffers = usePersistedDataStore((state) => state.setEmailingOffers);
  
  // Rounds state for resend dialog
  const [resendSelectedRound, setResendSelectedRound] = useState(1);
  const [showAdvancedSettings, setShowAdvancedSettings] = useState(false);
  
  // Calculate max rounds from negotiation data
  const getNegotiationObj = usePersistedDataStore((state) => state.negotiationObj);
  const maxRounds = Math.max(1, ...(getNegotiationObj?.cases || []).map(caseItem => {
    if (!caseItem.Negotiations || !Array.isArray(caseItem.Negotiations)) return 1;
    return Math.max(0, ...caseItem.Negotiations.map(n => n.round || 0)) + 1;
  }));

  // Helper function to check if a case has town offers
  const hasTownOffers = (caseItem) => {
    if (!caseItem.Negotiations || !Array.isArray(caseItem.Negotiations)) return false;
    
    return caseItem.Negotiations.some(negotiation => 
      negotiation.offers && Array.isArray(negotiation.offers) && 
      negotiation.offers.some(offer => offer.OfferedBy === 'Town')
    );
  };

  // Calculate counts for the resend dialog
  const calculateResendCounts = () => {
    if (!getNegotiationObj?.cases || !selectedRows) return { withTownOffers: 0, withoutTownOffers: 0 };
    
    const selectedCases = getNegotiationObj.cases.filter(caseItem => 
      selectedRows.some(row => row.parcel_id === caseItem.parcel_id)
    );
    
    const withTownOffers = selectedCases.filter(hasTownOffers).length;
    const withoutTownOffers = selectedCases.length - withTownOffers;
    
    return { withTownOffers, withoutTownOffers };
  };

  const { withTownOffers, withoutTownOffers } = calculateResendCounts();

  // Handle resend dialog cancel - clear emailing state
  const handleResendCancel = () => {
    setResendDialogOpen(false);
    setEmailingOffers(false);
  };
  
  // Handle resend confirmation with selected round
  const handleResendConfirmWithRound = () => {
    setResendDialogOpen(false);
    if (handleResendConfirm) {
      // Only pass the selected round if advanced settings is enabled
      // Otherwise pass null to use default behavior
      const roundToPass = showAdvancedSettings ? resendSelectedRound : null;
      handleResendConfirm(roundToPass);
    }
  };
  
  // Handle resend dialog open - reset to max round
  React.useEffect(() => {
    if (resendDialogOpen) {
      setResendSelectedRound(maxRounds);
    }
  }, [resendDialogOpen, maxRounds]);

  return (
    <>
      {/* Missing Offers Dialog */}
      <StableMuiDialog
        open={(offerFlowDialog?.type === 'missing_offers') || !!missingOffersDialogOpen}
        onClose={updatingOffersToWorkup ? undefined : () => {
          // Controlled: only close via explicit buttons (Cancel/Set to Workup).
          if (setOfferFlowDialog) return;
          setMissingOffersDialogOpen(false);
        }}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>{updatingOffersToWorkup ? 'Updating...' : 'Missing Offers'}</DialogTitle>
        <DialogContent>
          {updatingOffersToWorkup ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 3 }}>
              <CircularProgress sx={{ mb: 2 }} />
              <DialogContentText>
                Setting offers to workup values and finalizing cases...
              </DialogContentText>
            </Box>
          ) : (
            <>
              <DialogContentText>
                The following {(setOfferFlowDialog ? offerFlowSelectedRows : selectedRows).length > 0 ? 'selected ' : ''}properties are missing offers and cannot be finalized:
              </DialogContentText>
              <Box sx={{ mt: 2, maxHeight: 300, overflow: 'auto' }}>
                {(setOfferFlowDialog ? offerFlowMissingOffersList : missingOffersList).map((address, index) => (
                  <Box key={index} sx={{ py: 1, borderBottom: '1px solid #eee' }}>
                    {address}
                  </Box>
                ))}
              </Box>
              <DialogContentText sx={{ mt: 2, fontStyle: 'italic' }}>
                You can set these missing offers to their workup values to proceed with finalization.
              </DialogContentText>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={handleSetOffersToWorkup} 
            variant="text"
            disabled={updatingOffersToWorkup}
          >
            {updatingOffersToWorkup ? 'Updating...' : 'Set to Workup Values'}
          </Button>
          <Button 
            onClick={() => {
              if (setOfferFlowDialog) return setOfferFlowDialog(null);
              setMissingOffersDialogOpen(false);
            }} 
            color="primary" 
            variant="contained"
            disabled={updatingOffersToWorkup}
          >
            Set Manually
          </Button>
        </DialogActions>
      </StableMuiDialog>

      {/* Missing Comps Dialog */}
      <StableMuiDialog
        open={(offerFlowDialog?.type === 'missing_comps') || !!missingCompsDialogOpen}
        onClose={() => {
          // Controlled: only close via explicit buttons (Cancel/Set Comps).
          if (setOfferFlowDialog) return;
          setMissingCompsDialogOpen(false);
        }}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Missing Comps</DialogTitle>
        <DialogContent>
          <DialogContentText>
            The following {(setOfferFlowDialog ? offerFlowSelectedRows : selectedRows).length > 0 ? 'selected ' : ''}properties are missing comps data:
          </DialogContentText>
          <Box sx={{ mt: 2, maxHeight: 300, overflow: 'auto' }}>
            {(setOfferFlowDialog ? offerFlowMissingCompsList : missingCompsList).map((address, index) => (
              <Box key={index} sx={{ py: 1, borderBottom: '1px solid #eee' }}>
                {address}
              </Box>
            ))}
          </Box>
          <DialogContentText sx={{ mt: 2, fontStyle: 'italic' }}>
            These properties cannot be included until comps are set.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            if (setOfferFlowDialog) return setOfferFlowDialog(null);
            setMissingCompsDialogOpen(false);
          }} variant="text">
            Cancel
          </Button>
          <Button onClick={() => {
            if (onMissingCompsSet) {
              onMissingCompsSet(0);
            }
            if (setOfferFlowDialog) return setOfferFlowDialog(null);
            setMissingCompsDialogOpen(false);
          }} color="primary" variant="contained">
            Set Comps
          </Button>
        </DialogActions>
      </StableMuiDialog>

      <StableMuiDialog
        open={(offerFlowDialog?.type === 'workups_above_ifmv') || !!workupsAboveIfmvDialogOpen}
        onClose={() => {
          if (setOfferFlowDialog) return;
          setWorkupsAboveIfmvDialogOpen?.(false);
        }}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Workups above IFMV</DialogTitle>
        <DialogContent>
          <DialogContentText>
            The following properties have workup values above IFMV. If you continue without updating them, those workups will be omitted from what is sent to the town.
          </DialogContentText>
          <Box sx={{ mt: 2, maxHeight: 300, overflow: 'auto' }}>
            {(offerFlowDialog?.workupsAboveIfmv || workupsAboveIfmvList).map((item, index) => {
              const workup = Number(item.ProprietyWorkup ?? item.workupValue);
              const ifmv = Number(item.IFMV ?? item.ifmv);
              const hasNums = Number.isFinite(workup) && Number.isFinite(ifmv);
              const delta = hasNums ? Math.round(workup - ifmv) : null;
              return (
                <Box key={item.parcel_id || index} sx={{ py: 1, borderBottom: '1px solid #eee' }}>
                  <Typography component="div">{item.Address || item.address || item.parcel_id}</Typography>
                  {hasNums && (
                    <Typography component="div" variant="body2" sx={{ mt: 0.25 }}>
                      {`$${Math.round(workup).toLocaleString()} workup is `}
                      <Box component="span" sx={{ color: 'error.main', fontWeight: 700 }}>
                        {`${delta > 0 ? '+' : ''}${delta.toLocaleString()}`}
                      </Box>
                      {` above IFMV of $${Math.round(ifmv).toLocaleString()}`}
                    </Typography>
                  )}
                </Box>
              );
            })}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              if (setOfferFlowDialog) return setOfferFlowDialog(null);
              setWorkupsAboveIfmvDialogOpen?.(false);
            }}
            variant="text"
          >
            Cancel
          </Button>
          <Button
            onClick={() => {
              if (handleContinueWorkupsAboveIfmv) handleContinueWorkupsAboveIfmv();
              else if (setOfferFlowDialog) setOfferFlowDialog(null);
              else setWorkupsAboveIfmvDialogOpen?.(false);
            }}
            color="warning"
            variant="contained"
          >
            Continue anyway
          </Button>
        </DialogActions>
      </StableMuiDialog>

      {/* Subset Warning Dialog */}
      <StableMuiDialog
        open={(offerFlowDialog?.type === 'subset_warning') || !!subsetWarningDialogOpen}
        onClose={() => {
          // Controlled: only close via explicit buttons (Cancel/Send Selected/Send All).
          if (setOfferFlowDialog) return;
          setSubsetWarningDialogOpen(false);
        }}
        aria-labelledby="subset-warning-dialog-title"
        aria-describedby="subset-warning-dialog-description"
        maxWidth="md"
        fullWidth
      >
        <DialogTitle id="subset-warning-dialog-title">
          Open Cases Not Included
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="subset-warning-dialog-description">
            You are about to email offers for {(setOfferFlowDialog ? offerFlowSelectedRows : selectedRows).length} selected case(s), but there are {(setOfferFlowDialog ? offerFlowOpenCases : openCases).length} open case(s) that are not included in this selection.
          </DialogContentText>
          <Box sx={{ mt: 2, maxHeight: 300, overflow: 'auto' }}>
            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold' }}>
              Open cases not included:
            </Typography>
            {(setOfferFlowDialog ? offerFlowOpenCases : openCases).map((caseItem, index) => (
              <Box key={index} sx={{ py: 1, borderBottom: '1px solid #eee' }}>
                {caseItem.Address || caseItem.parcel_id}
              </Box>
            ))}
          </Box>
          <DialogContentText sx={{ mt: 2, fontStyle: 'italic' }}>
            These cases have offers that are finalized and ready to be sent, but they are not included in your current selection. 
            You can either send only the selected cases or send all cases (selected + open cases).
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => {
              if (setOfferFlowDialog) return setOfferFlowDialog(null);
              setSubsetWarningDialogOpen(false);
            }}
            color="primary"
            disabled={emailingOffers}
          >
            Cancel
          </Button>
          <Button 
            onClick={() => {
              if (setOfferFlowDialog) return setOfferFlowDialog({ type: 'send_selected_confirm' });
              handleSendSelectedClick();
            }}
            color="error"
            variant="outlined"
            disabled={emailingOffers}
            startIcon={emailingOffers ? <CircularProgress size={16} /> : <SendIcon />}
          >
            {emailingOffers ? 'Sending...' : 'Send Selected'}
          </Button>
          <Button 
            onClick={handleSendAllFinalized}
            color="primary"
            variant="contained"
            disabled={emailingOffers}
            startIcon={emailingOffers ? <CircularProgress size={16} /> : <SendIcon />}
          >
            {emailingOffers ? 'Sending...' : `Send All Cases (${(setOfferFlowDialog ? offerFlowSelectedRows : selectedRows).length + (setOfferFlowDialog ? offerFlowOpenCases : openCases).length})`}
          </Button>
        </DialogActions>
      </StableMuiDialog>

      {/* Send Selected Confirmation Dialog */}
      <StableMuiDialog
        open={(offerFlowDialog?.type === 'send_selected_confirm') || !!sendSelectedConfirmDialogOpen}
        onClose={() => {
          // Controlled: only close via explicit buttons.
          if (setOfferFlowDialog) return;
          setSendSelectedConfirmDialogOpen(false);
        }}
        aria-labelledby="send-selected-confirm-dialog-title"
        aria-describedby="send-selected-confirm-dialog-description"
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle id="send-selected-confirm-dialog-title" sx={{ color: 'error.main' }}>
          DANGER: Sending a sub-selection of cases
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="send-selected-confirm-dialog-description" sx={{ fontSize: '1rem', fontWeight: 'bold' }}>
          Recommended practice is to include ALL cases with offers when sending emails to avoid confusion.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => {
              if (setOfferFlowDialog) return setOfferFlowDialog(null);
              setSendSelectedConfirmDialogOpen(false);
            }}
            color="primary"
            variant="outlined"
          >
            Cancel
          </Button>
          <Button 
            onClick={handleConfirmSendSelected}
            color="error"
            variant="contained"
            startIcon={<SendIcon />}
          >
            Send Selected Anyway
          </Button>
        </DialogActions>
      </StableMuiDialog>

      {/* Unified Cancel Dialog */}
      <UnifiedCancelDialog
        open={unifiedCancelDialogOpen}
        onClose={() => setUnifiedCancelDialogOpen(false)}
        rowsToCancel={rowsToCancel}
        cancellingOffers={cancellingOffers}
        onConfirm={handleUnifiedCancelConfirm}
        getNegotiationObj={getNegotiationObj}
      />

      {/* Upload Dialog */}
      {uploadDialogOpen && (
        <StableMuiDialog open={uploadDialogOpen} onClose={() => setUploadDialogOpen(false)} maxWidth="sm" fullWidth>
          <DialogTitle>
            {townView ? 'Upload Town Responses' : 'Import Town Offers'}
          </DialogTitle>
          <DialogContent>
            <DialogContentText>
              Uploaded spreadsheet must match the downloaded template. The file will be validated for required fields and data integrity.
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setUploadDialogOpen(false)}>
              Cancel
            </Button>
            <Button 
              onClick={onFileUpload}
              variant="contained"
            >
              Browse Files
            </Button>
          </DialogActions>
        </StableMuiDialog>
      )}

      {assignmentDialogOpen && DoNotFileScarDialog && (
        <DoNotFileScarDialog
          open={assignmentDialogOpen}
          onClose={() => setAssignmentDialogOpen(false)}
        />
      )}

      {/* Email Preview Dialog */}
      {emailPreviewDialogComponent}

      {/* Offer Resend Dialog */}
      <StableMuiDialog
        open={resendDialogOpen}
        onClose={handleResendCancel}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Offers Already Sent
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            {alreadySentCount} offers have already been sent to the assessor. Sending again will:
          </DialogContentText>
          
          {/* Detailed breakdown of what will happen */}
          <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
            <Typography variant="body2" sx={{ fontWeight: 500, mb: 1 }}>
              What will happen:
            </Typography>
            {withoutTownOffers > 0 && (
              <Typography variant="body2" sx={{ mb: 1 }}>
                • <strong>{withoutTownOffers} cases</strong> will have their offers updated for the current round
              </Typography>
            )}
            {withTownOffers > 0 && (
              <Typography variant="body2" sx={{ mb: 1 }}>
                • <strong>{withTownOffers} cases</strong> will create new offers for the next round because town offers exist
              </Typography>
            )}
            {withoutTownOffers === 0 && withTownOffers === 0 && (
              <Typography variant="body2" sx={{ mb: 1 }}>
                • All selected cases will be processed based on their current negotiation status
              </Typography>
            )}
          </Box>
          
          {/* Advanced Settings Toggle */}
          <Box sx={{ mt: 2, mb: 1 }}>
            <FormControlLabel
              control={
                <Switch
                  checked={showAdvancedSettings}
                  onChange={(e) => setShowAdvancedSettings(e.target.checked)}
                  size="small"
                />
              }
              label={
                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                  Advanced Settings
                </Typography>
              }
            />
          </Box>
          
          {/* Rounds Selection - Only shown when advanced settings is enabled */}
          <Collapse in={showAdvancedSettings}>
            <Box sx={{ mt: 1, mb: 2 }}>
              <FormControl fullWidth size="small">
                <InputLabel>Select Round</InputLabel>
                <Select
                  value={resendSelectedRound}
                  label="Select Round"
                  onChange={(e) => setResendSelectedRound(e.target.value)}
                >
                  {Array.from({ length: maxRounds }, (_, i) => i + 1).map((round) => (
                    <MenuItem key={round} value={round}>
                      Round {round}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1, fontSize: '12px' }}>
                This will create offers for Round {resendSelectedRound} of negotiations.
              </Typography>
            </Box>
          </Collapse>
          
          <Box sx={{ 
            mt: 2, 
            p: 2, 
            bgcolor: 'grey.50', 
            border: '1px solid', 
            borderColor: 'grey.300',
            borderRadius: 1 
          }}>
            <Typography variant="body2" color="text.secondary">
              <strong>Recommended Practice:</strong> Select all cases and send all offers at once to ensure consistency and efficiency.
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleResendCancel} color="primary">
            Cancel
          </Button>
          <Button onClick={handleResendConfirmWithRound} color="primary" variant="contained">
            Send Again
          </Button>
        </DialogActions>
      </StableMuiDialog>
    </>
  );
};

export default CaseReviewDialogs;
